import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Store as StoreIcon,
  Campaign as CampaignIcon,
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import apiService from '../../services/api';
import { 
  ComercioExtendido, 
  ComercioFilters, 
  PaginatedResponse,
  Departamento,
  Provincia,
  Distrito
} from '../../types/comercio';

const ComerciosExtendidos: React.FC = () => {
  const navigate = useNavigate();
  
  // Estados principales
  const [comercios, setComercios] = useState<ComercioExtendido[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filtros
  const [filtros, setFiltros] = useState<ComercioFilters>({});
  const [filtrosDialogOpen, setFiltrosDialogOpen] = useState(false);
  const [buscar, setBuscar] = useState('');
  
  // Para los selectores de ubigeo en filtros
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [distritos, setDistritos] = useState<Distrito[]>([]);

  // Estados para el modal de contraseña
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const [selectedComercio, setSelectedComercio] = useState<ComercioExtendido | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  useEffect(() => {
    loadComercios();
    loadDepartamentos();
  }, [page, rowsPerPage, filtros]);

  const loadComercios = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sort_by: 'razon_social',
        sort_order: 'ASC' as const,
        ...filtros,
        buscar: buscar || undefined
      };

      const response = await apiService.getComercios(params);
      
      if (response.success) {
        const data = response.data as PaginatedResponse<any>;
        setComercios(data.data);
        setTotalItems(data.pagination.total_items);
      } else {
        setError(response.message || 'Error cargando comercios');
      }
    } catch (error) {
      console.error('Error cargando comercios:', error);
      setError('Error cargando comercios');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartamentos = async () => {
    try {
      const response = await apiService.getDepartamentos();
      if (response.success) {
        setDepartamentos(response.data);
      }
    } catch (error) {
      console.error('Error cargando departamentos:', error);
    }
  };

  const loadProvincias = async (departamentoId: string) => {
    try {
      const response = await apiService.getProvincias(departamentoId);
      if (response.success) {
        setProvincias(response.data);
      }
    } catch (error) {
      console.error('Error cargando provincias:', error);
    }
  };

  const loadDistritos = async (provinciaId: string) => {
    try {
      const response = await apiService.getDistritos(provinciaId);
      if (response.success) {
        setDistritos(response.data);
      }
    } catch (error) {
      console.error('Error cargando distritos:', error);
    }
  };

  const handleBuscar = () => {
    setPage(0);
    loadComercios();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltroChange = (campo: keyof ComercioFilters, valor: any) => {
    const nuevosFiltros = { ...filtros, [campo]: valor };
    
    // Limpiar filtros dependientes
    if (campo === 'departamento_id') {
      nuevosFiltros.provincia_id = undefined;
      nuevosFiltros.distrito_id = undefined;
      setProvincias([]);
      setDistritos([]);
      if (valor) {
        loadProvincias(valor);
      }
    } else if (campo === 'provincia_id') {
      nuevosFiltros.distrito_id = undefined;
      setDistritos([]);
      if (valor) {
        loadDistritos(valor);
      }
    }
    
    setFiltros(nuevosFiltros);
  };

  const limpiarFiltros = () => {
    setFiltros({});
    setBuscar('');
    setProvincias([]);
    setDistritos([]);
    setPage(0);
  };

  const handleEliminar = async (id: number) => {
    if (window.confirm('¿Está seguro de desactivar este comercio?')) {
      try {
        const response = await apiService.deleteComercio(id);
        if (response.success) {
          loadComercios();
        } else {
          setError(response.message || 'Error desactivando comercio');
        }
      } catch (error) {
        setError('Error desactivando comercio');
      }
    }
  };

  const formatearDireccion = (comercio: ComercioExtendido): string => {
    const dir = comercio.direccion_principal || {};
    if (!dir.calle) return 'No registrada';
    return `${dir.calle}${dir.numero ? ` ${dir.numero}` : ''}${dir.distrito_nombre ? `, ${dir.distrito_nombre}` : ''}`;
  };

  const contarFiltrosActivos = (): number => {
    return Object.values(filtros).filter(valor => valor !== undefined && valor !== '').length + (buscar ? 1 : 0);
  };

  const handleGenerarClave = async (comercio: ComercioExtendido) => {
    if (!window.confirm(`¿Está seguro de generar una nueva contraseña para ${comercio.razon_social}?`)) {
      return;
    }

    setResetPasswordLoading(true);
    setSelectedComercio(comercio);
    
    try {
      const response = await apiService.resetComercioPassword(comercio.id);
      
      if (response.success) {
        setGeneratedPassword(response.data.temp_password);
        setPasswordModalOpen(true);
      } else {
        setError(response.message || 'Error generando nueva contraseña');
      }
    } catch (error) {
      console.error('Error generando contraseña:', error);
      setError('Error generando nueva contraseña');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      // Mostrar feedback de copiado (opcional)
    } catch (error) {
      console.error('Error copiando al portapapeles:', error);
    }
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
    setGeneratedPassword('');
    setSelectedComercio(null);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestión de Comercios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/comercios-ext/nuevo')}
        >
          Nuevo Comercio
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda y filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Buscar comercios"
            placeholder="RUC, razón social o email"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
            sx={{ minWidth: 300 }}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleBuscar} size="small">
                  <SearchIcon />
                </IconButton>
              )
            }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltrosDialogOpen(true)}
            sx={{ position: 'relative' }}
          >
            Filtros
            {contarFiltrosActivos() > 0 && (
              <Chip
                label={contarFiltrosActivos()}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20 }}
              />
            )}
          </Button>

          {contarFiltrosActivos() > 0 && (
            <Button variant="text" onClick={limpiarFiltros}>
              Limpiar filtros
            </Button>
          )}
        </Box>
      </Paper>

      {/* Tabla de comercios */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>RUC</TableCell>
              <TableCell>Razón Social</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Dirección</TableCell>
              <TableCell align="center">Locales</TableCell>
              <TableCell align="center">Campañas</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : comercios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No se encontraron comercios
                </TableCell>
              </TableRow>
            ) : (
              comercios.map((comercio) => (
                <TableRow key={comercio.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {comercio.ruc}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {comercio.razon_social}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {comercio.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {formatearDireccion(comercio)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<StoreIcon />}
                      label={comercio.total_locales || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<CampaignIcon />}
                      label={comercio.campañas_activas || 0}
                      size="small"
                      variant="outlined"
                      color={comercio.campañas_activas ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={comercio.activo ? 'Activo' : 'Inactivo'}
                      color={comercio.activo ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/comercios-ext/${comercio.id}/locales`)}
                        title="Gestionar locales"
                      >
                        <StoreIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/comercios-ext/${comercio.id}/campanas`)}
                        title="Gestionar campañas"
                      >
                        <CampaignIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleGenerarClave(comercio)}
                        title="Generar nueva contraseña"
                        disabled={resetPasswordLoading}
                        color="primary"
                      >
                        <KeyIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/comercios-ext/${comercio.id}/editar`)}
                        title="Editar"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEliminar(comercio.id)}
                        color="error"
                        title="Desactivar"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
        />
      </TableContainer>

      {/* Dialog de Filtros */}
      <Dialog
        open={filtrosDialogOpen}
        onClose={() => setFiltrosDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filtros Avanzados</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Departamento */}
            <FormControl fullWidth>
              <InputLabel>Departamento</InputLabel>
              <Select
                value={filtros.departamento_id || ''}
                label="Departamento"
                onChange={(e) => handleFiltroChange('departamento_id', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {departamentos.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Provincia */}
            <FormControl fullWidth disabled={!filtros.departamento_id}>
              <InputLabel>Provincia</InputLabel>
              <Select
                value={filtros.provincia_id || ''}
                label="Provincia"
                onChange={(e) => handleFiltroChange('provincia_id', e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {provincias.map((prov) => (
                  <MenuItem key={prov.id} value={prov.id}>
                    {prov.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Distrito */}
            <FormControl fullWidth disabled={!filtros.provincia_id}>
              <InputLabel>Distrito</InputLabel>
              <Select
                value={filtros.distrito_id || ''}
                label="Distrito"
                onChange={(e) => handleFiltroChange('distrito_id', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {distritos.map((dist) => (
                  <MenuItem key={dist.id} value={dist.id}>
                    {dist.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Estado activo */}
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filtros.activo !== undefined ? (filtros.activo ? 'true' : 'false') : ''}
                label="Estado"
                onChange={(e) => {
                  const value = e.target.value as string;
                  handleFiltroChange('activo', !value ? undefined : value === 'true');
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </Select>
            </FormControl>

            {/* Filtros booleanos */}
            <FormControlLabel
              control={
                <Switch
                  checked={filtros.con_locales || false}
                  onChange={(e) => handleFiltroChange('con_locales', e.target.checked || undefined)}
                />
              }
              label="Solo comercios con locales"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={filtros.con_campañas_activas || false}
                  onChange={(e) => handleFiltroChange('con_campañas_activas', e.target.checked || undefined)}
                />
              }
              label="Solo comercios con campañas activas"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={limpiarFiltros}>Limpiar</Button>
          <Button onClick={() => setFiltrosDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal para mostrar contraseña generada */}
      <Dialog
        open={passwordModalOpen}
        onClose={handleClosePasswordModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Nueva Contraseña Generada
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Se ha generado una nueva contraseña para {selectedComercio?.razon_social}
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              RUC: {selectedComercio?.ruc}
            </Typography>
            
            <Paper sx={{ p: 2, bgcolor: 'grey.100', mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Nueva contraseña temporal:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ fontFamily: 'monospace', flex: 1 }}
                >
                  {generatedPassword}
                </Typography>
                <IconButton onClick={handleCopyPassword} color="primary" title="Copiar al portapapeles">
                  <CopyIcon />
                </IconButton>
              </Box>
            </Paper>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Por favor, proporcione esta contraseña al comercio de manera segura.
              El comercio deberá cambiarla en su primer acceso.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordModal} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default ComerciosExtendidos;
